// src/features/task/create/PropertyRow.tsx
// One labelled row of the always-open properties panel.
//
// The panel answers "what did you choose?", so every property keeps its place
// whether or not it is set: the label is always visible and the control below
// it reads "not set" while empty. A row never disappears because its source
// list came back empty — a property that *can* be set is information even when
// it is not set yet, and a vanishing row is how people stop believing a panel.
//
// The row owns the label, the required marker and the message; the control
// inside owns the value. That split is what lets the `row` variant of
// PropertyPill stay a single full-width value.

import type { ReactNode } from 'react';
import { cn } from '@core/ui';

export interface PropertyRowProps {
  label: string;
  /** Shows the required marker, and reads as required to assistive tech. */
  required?: boolean;
  /** Validation message, rendered under the control and announced. */
  error?: string;
  /** DOM id of that message, so the control can point at it. */
  errorId?: string;
  /** One short line under the control — used sparingly, never as a lecture. */
  hint?: string;
  className?: string;
  children: ReactNode;
}

export function PropertyRow({
  label,
  required,
  error,
  errorId,
  hint,
  className,
  children,
}: PropertyRowProps) {
  return (
    <div
      className={cn('flex flex-col gap-1', className)}
      data-field-error={error ? 'true' : undefined}
    >
      <span className="px-2 text-xs font-medium text-muted-foreground">
        {label}
        {required && (
          <span className="ml-0.5 text-destructive" aria-hidden="true">
            *
          </span>
        )}
      </span>
      {children}
      {error ? (
        <p id={errorId} role="alert" className="px-2 text-xs text-destructive">
          {error}
        </p>
      ) : (
        hint && <p className="px-2 text-xs text-muted-foreground/80">{hint}</p>
      )}
    </div>
  );
}
